/**
 * MaterialReadinessChip — Phase 3 canonical install-readiness display.
 *
 * Read-only display over `resolveMaterialReadiness()`. Compact chip for
 * cross-domain surfaces (Install Readiness / Scheduling / Job Detail /
 * Install Today). Single deterministic verdict — distinct from the
 * Phase 1B procurement chip which shows multi-faceted health.
 */
import type { Job } from "@/data/types";
import type {
  MaterialReadinessSeverity,
  MaterialReadinessVerdict,
} from "@/data/procurement/materialReadinessResolver";
import { useMaterialReadiness } from "@/data/procurement/useMaterialReadiness";
import { Badge } from "@/components/ui/badge";
import { Package, AlertTriangle, XCircle, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";

function severityClass(s: MaterialReadinessSeverity): string {
  if (s === "blocker") return "bg-destructive/10 text-destructive ring-1 ring-destructive/30 border-destructive/30";
  if (s === "warn") return "bg-warning/10 text-warning ring-1 ring-warning/30 border-warning/30";
  if (s === "info") return "bg-success/10 text-success ring-1 ring-success/30 border-success/30";
  return "bg-muted/40 text-muted-foreground ring-1 ring-border/60 border-border/60";
}

function severityIcon(v: MaterialReadinessVerdict) {
  if (v.severity === "blocker") return <XCircle className="h-3.5 w-3.5" />;
  if (v.severity === "warn") return <AlertTriangle className="h-3.5 w-3.5" />;
  if (v.installReady) return <CheckCircle2 className="h-3.5 w-3.5" />;
  return <Package className="h-3.5 w-3.5" />;
}

export type MaterialReadinessChipVariant =
  | "readiness"
  | "scheduling"
  | "installOps"
  | "summary";

interface ChipProps {
  job: Job;
  variant?: MaterialReadinessChipVariant;
  className?: string;
  /** Hide when state is `not_required`, or for `installOps` when not warn/blocker. */
  hideWhenSilent?: boolean;
}

function labelFor(v: MaterialReadinessVerdict, variant: MaterialReadinessChipVariant): string {
  if (variant === "readiness") return v.readinessLabel;
  if (variant === "scheduling") return v.schedulingLabel;
  if (variant === "installOps") return v.installOpsLabel;
  return v.summaryLabel;
}

export function MaterialReadinessChip({
  job,
  variant = "summary",
  className,
  hideWhenSilent = false,
}: ChipProps) {
  const v = useMaterialReadiness(job);
  const silent =
    v.readinessState === "not_required" ||
    (variant === "installOps" && v.severity !== "warn" && v.severity !== "blocker");
  if (hideWhenSilent && silent) return null;
  const label = labelFor(v, variant);
  return (
    <Badge
      variant="outline"
      className={cn("gap-1 text-[10px] font-medium", severityClass(v.severity), className)}
      title={label}
      data-testid={`material-readiness-chip-${variant}`}
    >
      {severityIcon(v)}
      <span className="truncate max-w-[220px]">{label}</span>
    </Badge>
  );
}
